// CarteProfessionnelle.jsx – Téléchargement en PNG (html2canvas)
import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faIdCard, faDownload, faTimes, faSpinner, faCheckCircle,
  faUser, faBriefcase, faCalendarAlt, faBuilding, faPhone,
  faEnvelope, faMapMarkerAlt
} from '@fortawesome/free-solid-svg-icons';
import html2canvas from 'html2canvas';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import defaultLogo from '../../../assetes/logos.png';
import defaultAvatar from '../../../assetes/profile.png';

const API_URL = `${API_CONFIG2.apiUrl}employe/api_attestation.php`;

const CarteProfessionnelle = ({ employeId }) => {
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [carteData, setCarteData] = useState(null);
  const [generatingImage, setGeneratingImage] = useState(false);
  const carteRef = useRef(null);
  const modalBodyRef = useRef(null);

  useEffect(() => {
    if (employeId) loadData();
  }, [employeId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}?employe_id=${employeId}`);
      const json = await res.json();
      if (json.status === 'success') {
        setCarteData(json.data);
      } else {
        alert('Erreur : ' + (json.message || 'Données non trouvées'));
      }
    } catch (err) {
      alert('Erreur réseau : ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadImage = async () => {
    if (!carteRef.current) return;
    setGeneratingImage(true);
    try {
      const element = carteRef.current;
      const modalBody = modalBodyRef.current;

      // Sauvegarder les styles originaux
      const originalModalOverflow = modalBody ? modalBody.style.overflow : '';
      const originalElementOverflow = element.style.overflow;
      const originalElementHeight = element.style.height;
      const originalElementMaxHeight = element.style.maxHeight;

      if (modalBody) modalBody.style.overflow = 'visible';
      element.style.overflow = 'visible';
      element.style.height = 'auto';
      element.style.maxHeight = 'none';
      element.scrollTop = 0;

      const canvas = await html2canvas(element, {
        scale: 2.5,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        allowTaint: false,
        windowHeight: element.scrollHeight,
        windowWidth: element.scrollWidth,
        onclone: (clonedDoc) => {
          const clonedEl = clonedDoc.querySelector('[data-carte]');
          if (clonedEl) {
            clonedEl.style.overflow = 'visible';
            clonedEl.style.height = 'auto';
            clonedEl.style.maxHeight = 'none';
          }
        }
      });

      // Restaurer les styles
      if (modalBody) modalBody.style.overflow = originalModalOverflow || 'auto';
      element.style.overflow = originalElementOverflow || '';
      element.style.height = originalElementHeight || '';
      element.style.maxHeight = originalElementMaxHeight || '';

      // Télécharger en PNG
      const link = document.createElement('a');
      link.download = `Carte_${carteData.employe.nom || 'employe'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('Erreur capture:', error);
      alert('Erreur lors de la génération de l\'image : ' + error.message);
    } finally {
      setGeneratingImage(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const employe = carteData?.employe;
  const entreprise = carteData?.entreprise || {};
  const logoUrl = entreprise.logoUrl || defaultLogo;
  const photoPath = employe?.photo ? `${API_CONFIG2.apiUrl}${employe.photo}` : defaultAvatar;
  const badgeId = `EMP-${String(employe?.id || '').padStart(4, '0')}`;

  return (
    <>
      {/* Bouton d'ouverture */}
      <button
        className="btn-carte"
        onClick={() => setShowModal(true)}
        disabled={loading}
        style={{
          padding: '12px 28px',
          borderRadius: '40px',
          border: 'none',
          background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)',
          color: '#fff',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '10px',
          transition: 'all 0.3s ease',
          fontWeight: '600',
          fontSize: '0.95rem',
          boxShadow: '0 4px 14px rgba(30, 58, 138, 0.4)',
        }}
        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
      >
        {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faIdCard} />}
        {loading ? 'Chargement...' : '🪪 Carte professionnelle'}
      </button>

      {/* Modal */}
      {showModal && carteData && (
        <div className="carte-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="carte-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="carte-modal-header">
              <h3>
                <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#22c55e', marginRight: '8px' }} />
                Carte professionnelle
              </h3>
              <button className="close-btn" onClick={() => setShowModal(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="carte-modal-body" ref={modalBodyRef}>
              <div className="carte-card" ref={carteRef} data-carte>
                <div className="card-container">
                  {/* En-tête */}
                  <div className="card-header">
                    <div className="logo-wrapper">
                      <img src={logoUrl} alt="Logo" className="logo" crossOrigin="anonymous" />
                    </div>
                    <div className="entreprise-info">
                      <h1>{entreprise.nom || 'Nom de l\'entreprise'}</h1>
                      <p>
                        <FontAwesomeIcon icon={faMapMarkerAlt} style={{ marginRight: '4px' }} />
                        {entreprise.adresse || ''}
                      </p>
                      <p className="contact">
                        {entreprise.telephone && (
                          <span><FontAwesomeIcon icon={faPhone} /> Tél: {entreprise.telephone}</span>
                        )}
                        {entreprise.email && (
                          <span><FontAwesomeIcon icon={faEnvelope} /> Email: {entreprise.email}</span>
                        )}
                      </p>
                    </div>
                  </div>

                  {/* Corps */}
                  <div className="card-body">
                    <div className="photo-wrapper">
                      <img src={photoPath} alt="Photo" className="photo" crossOrigin="anonymous" />
                    </div>
                    <div className="infos">
                      <div className="nom">
                        {employe.prenom} {employe.nom} {employe.postnom || ''}
                      </div>
                      <div className="fonction">
                        <FontAwesomeIcon icon={faBriefcase} style={{ marginRight: '6px' }} />
                        {employe.fonction || 'Non spécifié'}
                      </div>
                      <div className="service">
                        <FontAwesomeIcon icon={faBuilding} style={{ marginRight: '6px' }} />
                        {employe.service || 'Non spécifié'}
                      </div>
                      <div className="badge-id">
                        <span>BADGE N°</span>
                        <strong>{badgeId}</strong>
                      </div>
                      <div className="date-embauche">
                        <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: '6px' }} />
                        <span>Embauché le :</span> {formatDate(employe.date_embauche)}
                      </div>
                    </div>
                  </div>

                  {/* Pied */}
                  <div className="card-footer">
                    <span>Signature et cachet de l'entreprise</span>
                    <span className="validite">Validité : illimitée</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="carte-modal-footer">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>Fermer</button>
              <button className="btn-primary" onClick={handleDownloadImage} disabled={generatingImage}>
                {generatingImage ? (
                  <><FontAwesomeIcon icon={faSpinner} spin /> Génération...</>
                ) : (
                  <><FontAwesomeIcon icon={faDownload} /> Télécharger PNG</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Styles CSS (inchangés) */}
      <style jsx>{`
        .carte-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 20px;
          animation: fadeIn 0.3s ease;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.96); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes slideUp {
          from { transform: translateY(30px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .carte-modal-content {
          background: #ffffff;
          border-radius: 28px;
          max-width: 880px;
          width: 100%;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 40px 100px rgba(0, 0, 0, 0.4);
          overflow: hidden;
          animation: slideUp 0.4s ease;
        }
        .carte-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 18px 28px;
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          flex-shrink: 0;
        }
        .carte-modal-header h3 {
          margin: 0;
          font-size: 1.2rem;
          color: #0f172a;
          font-weight: 600;
          display: flex;
          align-items: center;
        }
        .close-btn {
          background: none;
          border: none;
          font-size: 1.6rem;
          cursor: pointer;
          color: #94a3b8;
          transition: 0.2s;
          padding: 0 4px;
        }
        .close-btn:hover {
          color: #ef4444;
          transform: rotate(90deg);
        }
        .carte-modal-body {
          flex: 1;
          overflow-y: auto;
          padding: 20px 24px 16px;
          background: #f1f5f9;
          display: flex;
          justify-content: center;
          align-items: center;
          transition: overflow 0.3s;
        }
        .carte-card {
          max-width: 750px;
          width: 100%;
          padding: 12px;
          background: transparent;
          display: flex;
          justify-content: center;
        }
        .card-container {
          width: 100%;
          max-width: 650px;
          background: linear-gradient(145deg, #ffffff, #eef2ff);
          border-radius: 28px;
          box-shadow: 0 16px 48px rgba(30, 58, 138, 0.15);
          overflow: hidden;
          border: 2px solid rgba(59, 130, 246, 0.15);
          transition: 0.3s;
        }
        .card-container:hover {
          box-shadow: 0 20px 60px rgba(30, 58, 138, 0.25);
        }
        .card-header {
          background: linear-gradient(135deg, #1e3a8a, #3b82f6);
          padding: 18px 22px;
          display: flex;
          align-items: center;
          gap: 16px;
          color: #ffffff;
        }
        .logo-wrapper {
          flex-shrink: 0;
          background: #ffffff;
          border-radius: 12px;
          padding: 6px 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        .logo {
          max-width: 70px;
          max-height: 60px;
          object-fit: contain;
        }
        .entreprise-info {
          flex: 1;
          line-height: 1.4;
        }
        .entreprise-info h1 {
          font-size: 22px;
          font-weight: 700;
          margin: 0 0 2px 0;
          color: #ffffff;
          letter-spacing: 0.5px;
        }
        .entreprise-info p {
          margin: 2px 0;
          font-size: 13px;
          opacity: 0.9;
        }
        .entreprise-info .contact {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          font-size: 12px;
          opacity: 0.8;
        }
        .entreprise-info .contact span {
          display: inline-flex;
          align-items: center;
          gap: 4px;
        }
        .card-body {
          padding: 20px 22px 18px;
          display: flex;
          gap: 24px;
          align-items: center;
          background: #ffffff;
        }
        .photo-wrapper {
          flex-shrink: 0;
          width: 110px;
          height: 110px;
          border-radius: 50%;
          border: 4px solid #3b82f6;
          box-shadow: 0 4px 16px rgba(59, 130, 246, 0.25);
          overflow: hidden;
          background: #e0e7ff;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: 0.3s;
        }
        .photo-wrapper:hover {
          transform: scale(1.02);
          border-color: #1e3a8a;
        }
        .photo {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .infos {
          flex: 1;
          color: #1e293b;
        }
        .nom {
          font-size: 22px;
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 4px;
        }
        .fonction {
          font-size: 16px;
          font-weight: 600;
          color: #1e3a8a;
          margin-bottom: 2px;
        }
        .service {
          font-size: 15px;
          color: #475569;
          margin-bottom: 8px;
        }
        .badge-id {
          display: inline-block;
          background: #e0e7ff;
          padding: 4px 16px;
          border-radius: 20px;
          font-size: 13px;
          color: #1e3a8a;
          margin-bottom: 4px;
          font-weight: 500;
        }
        .badge-id strong {
          font-weight: 700;
          margin-left: 4px;
        }
        .date-embauche {
          font-size: 14px;
          color: #475569;
        }
        .date-embauche span {
          font-weight: 500;
        }
        .card-footer {
          background: #f8fafc;
          border-top: 1px solid #e2e8f0;
          padding: 12px 22px;
          display: flex;
          justify-content: space-between;
          font-size: 13px;
          color: #475569;
        }
        .card-footer .validite {
          color: #3b82f6;
          font-weight: 600;
        }
        .carte-modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 14px;
          padding: 16px 28px;
          background: #f8fafc;
          border-top: 1px solid #e2e8f0;
          flex-shrink: 0;
          flex-wrap: wrap;
        }
        .btn-primary, .btn-secondary {
          padding: 10px 24px;
          border-radius: 40px;
          border: none;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.25s ease;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 0.9rem;
        }
        .btn-primary {
          background: #3b82f6;
          color: #fff;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }
        .btn-primary:hover:not(:disabled) {
          background: #1d4ed8;
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
        }
        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }
        .btn-secondary {
          background: #e2e8f0;
          color: #1e293b;
        }
        .btn-secondary:hover {
          background: #cbd5e1;
          transform: translateY(-2px);
        }
        @media (max-width: 640px) {
          .carte-modal-content {
            max-height: 100vh;
            border-radius: 0;
            max-width: 100%;
          }
          .carte-modal-body {
            padding: 12px;
          }
          .card-container {
            border-radius: 16px;
          }
          .card-header {
            flex-direction: column;
            text-align: center;
            padding: 16px;
          }
          .entreprise-info .contact {
            justify-content: center;
          }
          .card-body {
            flex-direction: column;
            text-align: center;
            padding: 16px;
          }
          .photo-wrapper {
            width: 90px;
            height: 90px;
          }
          .card-footer {
            flex-direction: column;
            align-items: center;
            gap: 4px;
            text-align: center;
          }
          .carte-modal-footer {
            flex-direction: column-reverse;
          }
          .btn-primary, .btn-secondary {
            justify-content: center;
            width: 100%;
          }
        }
        @media (max-width: 480px) {
          .entreprise-info h1 {
            font-size: 18px;
          }
          .nom {
            font-size: 18px;
          }
          .fonction {
            font-size: 14px;
          }
        }
      `}</style>
    </>
  );
};

export default CarteProfessionnelle;